import { UIEvent, useCallback, useEffect, useRef, useState } from 'react';

const SLIDE_SELECTOR = '.home-recommendation-carousel__slide';

const useRecommendationCarousel = (itemCount: number) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollFrameRef = useRef<number>();
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(1);

  const measure = useCallback(() => {
    const track = trackRef.current;
    const firstSlide = track?.querySelector<HTMLElement>(SLIDE_SELECTOR);

    if (!track || !firstSlide || firstSlide.offsetWidth === 0) return;

    const nextVisibleCount = Math.max(
      1,
      Math.min(
        itemCount,
        Math.round(track.clientWidth / firstSlide.offsetWidth)
      )
    );

    setVisibleCount(nextVisibleCount);
    setActiveIndex((current) =>
      Math.min(current, Math.max(0, itemCount - nextVisibleCount))
    );
  }, [itemCount]);

  useEffect(() => {
    measure();

    const track = trackRef.current;
    if (!track) return undefined;

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(track);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  useEffect(
    () => () => {
      if (scrollFrameRef.current !== undefined) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    },
    []
  );

  const updateActiveIndex = (event: UIEvent<HTMLDivElement>) => {
    if (scrollFrameRef.current !== undefined) {
      window.cancelAnimationFrame(scrollFrameRef.current);
    }

    const track = event.currentTarget;
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      const slides = Array.from(
        track.querySelectorAll<HTMLElement>(SLIDE_SELECTOR)
      );
      if (!slides.length) return;

      const closestIndex = slides.reduce((closest, slide, index) => {
        const currentDistance = Math.abs(
          slide.offsetLeft - slides[0].offsetLeft - track.scrollLeft
        );
        const closestDistance = Math.abs(
          slides[closest].offsetLeft - slides[0].offsetLeft - track.scrollLeft
        );
        return currentDistance < closestDistance ? index : closest;
      }, 0);

      setActiveIndex(
        Math.min(closestIndex, Math.max(0, itemCount - visibleCount))
      );
    });
  };

  const goTo = (direction: -1 | 1) => {
    const track = trackRef.current;
    if (!track) return;

    const slides = Array.from(
      track.querySelectorAll<HTMLElement>(SLIDE_SELECTOR)
    );
    const step = Math.max(1, visibleCount);
    const targetIndex = Math.max(
      0,
      Math.min(
        activeIndex + direction * step,
        Math.max(0, itemCount - visibleCount)
      )
    );
    const target = slides[targetIndex];
    if (!target) return;

    const left = target.offsetLeft - slides[0].offsetLeft;
    const reducedMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (typeof track.scrollTo === 'function') {
      track.scrollTo({ left, behavior: reducedMotion ? 'auto' : 'smooth' });
    } else {
      track.scrollLeft = left;
    }
    setActiveIndex(targetIndex);
  };

  return {
    activeIndex,
    goTo,
    trackRef,
    updateActiveIndex,
    visibleCount,
  };
};

export default useRecommendationCarousel;
